/**
 * TemplateBuilder Component
 *
 * Editor de un mensaje guardado de WhatsApp: nombre, idioma, encabezado
 * (texto/imagen/video/doc), cuerpo con {{N}} placeholders, footer y
 * hasta 3 botones de acción.
 *
 * Sin category Meta — al pasar a QR ya no aplica.
 */

import { useRef } from "react";
import {
  Tag,
  Globe,
  Heading2,
  AlignLeft,
  CircleDot,
  Plus,
  X,
  ExternalLink,
  PhoneCall,
  Reply,
  ImagePlus,
  Type as TypeIcon,
  Video,
  FileText,
  Sparkles,
} from "lucide-react";
import type {
  TemplateCategory,
  TemplateHeaderType,
  WhatsAppButton,
  WhatsAppButtonType,
  WhatsAppTemplateHeader,
} from "../types";

export interface TemplateDraft {
  name: string;
  category: TemplateCategory;
  language: string;
  header: WhatsAppTemplateHeader;
  body: string;
  footer: string;
  buttons: WhatsAppButton[];
}

interface TemplateBuilderProps {
  draft: TemplateDraft;
  onChange: (draft: TemplateDraft) => void;
  /** Lock the name field (when editing an existing template) */
  nameLocked?: boolean;
  /** Show an "AI suggest" CTA next to the body editor */
  onAISuggest?: () => void;
}

const HEADER_OPTIONS: {
  value: TemplateHeaderType;
  label: string;
  icon: typeof TypeIcon;
}[] = [
  { value: "none", label: "Sin encabezado", icon: AlignLeft },
  { value: "text", label: "Texto", icon: TypeIcon },
  { value: "image", label: "Imagen", icon: ImagePlus },
  { value: "video", label: "Video", icon: Video },
  { value: "document", label: "Documento", icon: FileText },
];

const BUTTON_TYPES: {
  value: WhatsAppButtonType;
  label: string;
  icon: typeof Reply;
}[] = [
  { value: "quick_reply", label: "Quick Reply", icon: Reply },
  { value: "url", label: "URL", icon: ExternalLink },
  { value: "phone_number", label: "Llamar", icon: PhoneCall },
];

const MAX_BODY = 1024;
const MAX_FOOTER = 60;
const MAX_NAME = 80;
const MAX_BUTTONS = 3;

export function TemplateBuilder({
  draft,
  onChange,
  nameLocked = false,
  onAISuggest,
}: TemplateBuilderProps) {
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const patch = (changes: Partial<TemplateDraft>) =>
    onChange({ ...draft, ...changes });

  /** Insert {{N}} at the cursor position in the body */
  const insertVariable = () => {
    const ta = bodyRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;

    // Compute next variable index = max existing + 1
    const existing = (draft.body.match(/\{\{(\d+)\}\}/g) || []).map((m) =>
      parseInt(m.replace(/\D/g, ""), 10),
    );
    const nextIdx = (existing.length ? Math.max(...existing) : 0) + 1;

    const insert = `{{${nextIdx}}}`;
    const newBody =
      draft.body.slice(0, start) + insert + draft.body.slice(end);
    patch({ body: newBody });

    // Restore caret after the inserted token (next tick — after re-render)
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + insert.length;
    });
  };

  const placeholderCount = (draft.body.match(/\{\{(\d+)\}\}/g) || []).length;

  /* ─── Header handlers ─── */
  const setHeaderType = (type: TemplateHeaderType) => {
    if (type === "none") {
      patch({ header: { type: "none" } });
    } else if (type === "text") {
      patch({ header: { type: "text", text: draft.header.text || "" } });
    } else {
      patch({ header: { type } });
    }
  };

  /* ─── Buttons handlers ─── */
  const addButton = (type: WhatsAppButtonType) => {
    if (draft.buttons.length >= MAX_BUTTONS) return;
    const newBtn: WhatsAppButton = {
      type,
      label: type === "quick_reply" ? "Responder" : "Ir al sitio",
      ...(type === "url" ? { url: "https://" } : {}),
      ...(type === "phone_number" ? { phoneNumber: "+54" } : {}),
    };
    patch({ buttons: [...draft.buttons, newBtn] });
  };

  const updateButton = (index: number, updates: Partial<WhatsAppButton>) => {
    const next = draft.buttons.map((b, i) =>
      i === index ? { ...b, ...updates } : b,
    );
    patch({ buttons: next });
  };

  const removeButton = (index: number) => {
    patch({ buttons: draft.buttons.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-6">
      {/* ─── Identity ─── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-5">
          <Tag size={18} className="text-emerald-500" />
          <h3 className="font-semibold text-slate-900">Identificación</h3>
        </div>

        <div className="space-y-4">
          {/* Name */}
          <div>
            <label className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-medium text-slate-700">
                Nombre interno
              </span>
              <span className="text-[10px] text-slate-400 font-mono">
                {draft.name.length}/{MAX_NAME}
              </span>
            </label>
            <input
              type="text"
              value={draft.name}
              onChange={(e) =>
                patch({
                  name: e.target.value.slice(0, MAX_NAME),
                })
              }
              disabled={nameLocked}
              placeholder="promo_descuento_mayo"
              className={`w-full px-4 py-3 border rounded-xl text-sm font-mono text-slate-900 placeholder:text-slate-400
                focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300 transition-all
                ${nameLocked ? "bg-slate-50 cursor-not-allowed" : "border-slate-200"}`}
            />
            <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
              Recomendado: snake_case minúsculas. Debe ser único por marca.
            </p>
          </div>

          {/* Language */}
          <div>
            <label className="flex items-center gap-1.5 mb-1.5">
              <Globe size={13} className="text-slate-400" />
              <span className="text-sm font-medium text-slate-700">Idioma</span>
            </label>
            <select
              value={draft.language}
              onChange={(e) => patch({ language: e.target.value })}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-900
                focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300 transition-all"
            >
              <option value="es_AR">Español (Argentina)</option>
              <option value="es_ES">Español (España)</option>
              <option value="es_MX">Español (México)</option>
              <option value="en_US">English (US)</option>
              <option value="en_GB">English (UK)</option>
              <option value="pt_BR">Português (Brasil)</option>
            </select>
          </div>
        </div>
      </div>

      {/* ─── Header ─── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-5">
          <Heading2 size={18} className="text-emerald-500" />
          <h3 className="font-semibold text-slate-900">Encabezado</h3>
          <span className="text-xs text-slate-400 ml-auto">Opcional</span>
        </div>

        <div className="grid grid-cols-5 gap-2 mb-4">
          {HEADER_OPTIONS.map((opt) => {
            const isActive = draft.header.type === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setHeaderType(opt.value)}
                className={`p-3 rounded-xl border-2 text-center transition-all ${
                  isActive
                    ? "border-emerald-300 bg-emerald-50"
                    : "border-slate-100 hover:border-slate-200"
                }`}
              >
                <opt.icon
                  size={16}
                  className={`mx-auto mb-1 ${
                    isActive ? "text-emerald-600" : "text-slate-400"
                  }`}
                />
                <p
                  className={`text-[10px] font-medium leading-tight ${
                    isActive ? "text-emerald-700" : "text-slate-600"
                  }`}
                >
                  {opt.label}
                </p>
              </button>
            );
          })}
        </div>

        {draft.header.type === "text" && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Texto del encabezado
            </label>
            <input
              type="text"
              value={draft.header.text || ""}
              onChange={(e) =>
                patch({
                  header: { type: "text", text: e.target.value.slice(0, 60) },
                })
              }
              placeholder="🎂 ¡Feliz cumple, {{1}}!"
              maxLength={60}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400
                focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300 transition-all"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Máx. 60 chars. Puede tener máximo 1 variable.
            </p>
          </div>
        )}

        {(draft.header.type === "image" ||
          draft.header.type === "video" ||
          draft.header.type === "document") && (
          <p className="text-xs text-slate-500 px-3 py-3 bg-slate-50 rounded-xl leading-relaxed">
            💡 El archivo concreto se sube al momento de enviar la campaña.
            Acá solo definís que la plantilla tendrá un encabezado de tipo{" "}
            <strong>{draft.header.type}</strong>.
          </p>
        )}
      </div>

      {/* ─── Body ─── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-3">
          <AlignLeft size={18} className="text-emerald-500" />
          <h3 className="font-semibold text-slate-900">Cuerpo del mensaje</h3>
          <span className="text-[10px] text-slate-400 ml-auto font-mono">
            {draft.body.length}/{MAX_BODY} · {placeholderCount} variables
          </span>
        </div>

        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <button
            type="button"
            onClick={insertVariable}
            className="flex items-center gap-1 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium rounded-lg transition-all"
          >
            <Plus size={11} />
            Insertar variable
          </button>
          {onAISuggest && (
            <button
              type="button"
              onClick={onAISuggest}
              className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-violet-50 to-fuchsia-50 hover:from-violet-100 hover:to-fuchsia-100 text-violet-700 text-xs font-medium rounded-lg transition-all"
            >
              <Sparkles size={11} />
              Sugerir con IA
            </button>
          )}
          <span className="text-[10px] text-slate-400 ml-auto">
            Soporta *negrita* _itálica_ ~tachado~ `code`
          </span>
        </div>

        <textarea
          ref={bodyRef}
          value={draft.body}
          onChange={(e) =>
            patch({ body: e.target.value.slice(0, MAX_BODY) })
          }
          rows={6}
          placeholder={
            "Hola {{1}}, tenemos una oferta especial para vos 🎉\n\n*{{2}}% OFF* en toda la tienda usando el código *{{3}}*\n\nVálido hasta fin de mes."
          }
          className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 resize-none font-mono leading-relaxed
            focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300 transition-all"
        />
      </div>

      {/* ─── Footer ─── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-3">
          <CircleDot size={18} className="text-emerald-500" />
          <h3 className="font-semibold text-slate-900">Pie de mensaje</h3>
          <span className="text-xs text-slate-400 ml-auto">Opcional</span>
        </div>

        <input
          type="text"
          value={draft.footer}
          onChange={(e) =>
            patch({ footer: e.target.value.slice(0, MAX_FOOTER) })
          }
          maxLength={MAX_FOOTER}
          placeholder="Si no querés recibir más promos, escribí BAJA."
          className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400
            focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300 transition-all"
        />
        <p className="text-[11px] text-slate-500 mt-1">
          Máx. {MAX_FOOTER} chars. Sin variables ni emojis. Útil para
          disclaimers legales.
        </p>
      </div>

      {/* ─── Buttons ─── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <CircleDot size={18} className="text-emerald-500" />
          <h3 className="font-semibold text-slate-900">Botones de acción</h3>
          <span className="text-xs text-slate-400 ml-auto">
            {draft.buttons.length}/{MAX_BUTTONS}
          </span>
        </div>

        {/* Existing buttons */}
        <div className="space-y-2 mb-4">
          {draft.buttons.map((btn, i) => {
            const meta = BUTTON_TYPES.find((t) => t.value === btn.type)!;
            return (
              <div
                key={i}
                className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl border border-slate-100"
              >
                <div className="w-7 h-7 bg-white rounded-md flex items-center justify-center flex-shrink-0">
                  <meta.icon size={13} className="text-slate-500" />
                </div>
                <input
                  type="text"
                  value={btn.label}
                  onChange={(e) =>
                    updateButton(i, { label: e.target.value.slice(0, 25) })
                  }
                  placeholder="Texto del botón"
                  maxLength={25}
                  className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white
                    focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300"
                />
                {btn.type === "url" && (
                  <input
                    type="url"
                    value={btn.url || ""}
                    onChange={(e) => updateButton(i, { url: e.target.value })}
                    placeholder="https://…"
                    className="flex-[1.5] px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white
                      focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300"
                  />
                )}
                {btn.type === "phone_number" && (
                  <input
                    type="tel"
                    value={btn.phoneNumber || ""}
                    onChange={(e) =>
                      updateButton(i, { phoneNumber: e.target.value })
                    }
                    placeholder="+5491100000000"
                    className="flex-[1.5] px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white
                      focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300"
                  />
                )}
                <button
                  type="button"
                  onClick={() => removeButton(i)}
                  className="w-8 h-8 flex-shrink-0 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all"
                >
                  <X size={15} />
                </button>
              </div>
            );
          })}
        </div>

        {/* Add buttons */}
        {draft.buttons.length < MAX_BUTTONS && (
          <div className="grid grid-cols-3 gap-2">
            {BUTTON_TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => addButton(t.value)}
                className="p-3 border-2 border-dashed border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/30 rounded-xl text-center transition-all group"
              >
                <t.icon
                  size={15}
                  className="text-slate-400 group-hover:text-emerald-500 mx-auto mb-1"
                />
                <p className="text-[10px] font-medium text-slate-600 group-hover:text-emerald-700">
                  {t.label}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default TemplateBuilder;
